import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface StoryContextType {
  context: Record<string, any> | null; // Holds route params for all pages
  setContext: (data: Record<string, any> | null) => void;
  resetContext: () => void;
}

const StoryContext = createContext<StoryContextType | undefined>(undefined);

interface StoryProviderProps {
  children: ReactNode;
}

const initialContextState = {
  sessionId: null,
  selectedStory: null,
  withImages: false,
};

export const StoryProvider: React.FC<StoryProviderProps> = ({ children }) => {
  const [context, setContextState] = useState<Record<string, any>>(initialContextState);
  const [loading, setLoading] = useState(true); // Track rehydration status

  // Rehydrate context from AsyncStorage on app load
  useEffect(() => {
    const loadContext = async () => {
      try {
        const savedContext = await AsyncStorage.getItem('storyContext');
        if (savedContext) {
          const parsedContext = JSON.parse(savedContext);
          setContextState(parsedContext);
          console.log('Rehydrated context:', parsedContext);
        } else {
          console.log('No saved context found in AsyncStorage.');
        }
      } catch (error) {
        console.error('Error rehydrating context from AsyncStorage:', error);
      } finally {
        setLoading(false); // Rehydration complete
      }
    };

    loadContext();
  }, []);

  // Save context to AsyncStorage whenever it changes
  const setContext = (data: Record<string, any> | null) => {
    if (data === null) {
      AsyncStorage.removeItem('storyContext');
      setContextState(initialContextState);
      console.log('Context reset to initial state.');
    } else {
      const newContext = { ...context, ...data };
      setContextState(newContext);
      AsyncStorage.setItem('storyContext', JSON.stringify(newContext))
        .then(() => console.log('Context saved to AsyncStorage:', newContext))
        .catch((err) => console.error('Error saving context to AsyncStorage:', err));
    }
  };

  const resetContext = () => {
    AsyncStorage.removeItem('storyContext');
    setContextState(initialContextState);
  };

  if (loading) {
    return null; // Prevent rendering until rehydration is complete
  }

  return (
    <StoryContext.Provider value={{ context, setContext, resetContext }}>
      {children}
    </StoryContext.Provider>
  );
};

export const useStory = () => {
  const storyContext = useContext(StoryContext);
  if (!storyContext) {
    throw new Error('useStory must be used within a StoryProvider');
  }
  return storyContext;
};
